import { useState, useEffect } from 'react';
import { C } from '../data';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../supabase';
import { fetchMyCoupons, fetchUserPoints, fetchPointTransactions } from '../api';
import { isValidName, isValidPhone, formatPhone, isValidZip, isValidAddress1, isValidPassword, PASSWORD_HINT } from '../utils/validators';
import Footer from './Footer';

const COUPON_STATUS_LABEL = {
  unused:  { text: '사용 가능', color: '#3D8B5C' },
  used:    { text: '사용 완료', color: '#888' },
  expired: { text: '만료',     color: '#C94040' },
};

const STATUS_LABEL = {
  pending:   { text: '입금 대기', color: '#E8A838' },
  confirmed: { text: '주문 확인', color: '#4A7AB5' },
  shipping:  { text: '배송 중',   color: '#3D8B5C' },
  delivered: { text: '배송 완료', color: '#888'    },
  cancelled: { text: '취소됨',   color: '#C94040' },
};

export default function MyPage({ onGoHome, nav }) {
  const { user, profile, signOut, refreshProfile } = useAuth();
  const [tab, setTab]       = useState('orders');
  const switchTab = async (key) => {
    setTab(key);
    if (key !== 'profile') { setProfileUnlocked(false); setLockPw(''); setLockMsg(''); }
    if (key === 'points' && user) {
      setPointsLoading(true);
      const [summary, txns, pending] = await Promise.all([
        fetchUserPoints(user.id).catch(() => ({ total_earned: 0, total_used: 0, available: 0 })),
        fetchPointTransactions(user.id).catch(() => []),
        supabase.from('reviews').select('review_type').eq('user_id', user.id).eq('photo_points_pending', true).then(({ data }) => data || []),
      ]);
      setPointSummary(summary);
      setPointTxns(txns);
      setPendingPoints(pending);
      setPointsLoading(false);
    }
  };
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(true);

  // 내 쿠폰
  const [coupons, setCoupons] = useState([]);
  const [couponsLoading, setCouponsLoading] = useState(true);

  // 적립금
  const [pointSummary,   setPointSummary]   = useState({ total_earned: 0, total_used: 0, available: 0 });
  const [pointTxns,      setPointTxns]      = useState([]);
  const [pointsLoading,  setPointsLoading]  = useState(false);
  const [pendingPoints,  setPendingPoints]  = useState([]);

  // 내 문의
  const [myInquiries, setMyInquiries] = useState([]);
  const [myInquiriesLoading, setMyInquiriesLoading] = useState(true);
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [inquiryReplies, setInquiryReplies] = useState([]);

  // 프로필 편집
  const [displayName, setDisplayName] = useState(profile?.name || '');
  const [phone,    setPhone]    = useState('');
  const [zip,      setZip]      = useState('');
  const [address1, setAddress1] = useState('');
  const [address2, setAddress2] = useState('');
  const [birthdate, setBirthdate] = useState('');
  const [gender,    setGender]    = useState('none');
  const [profileMsg,  setProfileMsg]  = useState('');
  const [profileSaving, setProfileSaving] = useState(false);

  useEffect(() => {
    if (!profile) return;
    setDisplayName(profile.name || '');
    setPhone(profile.phone || '');
    setZip(profile.zip || '');
    setAddress1(profile.address1 || '');
    setAddress2(profile.address2 || '');
    setBirthdate(profile.birthdate || '');
    setGender(profile.gender || 'none');
  }, [profile]);

  // 프로필 탭 비밀번호 잠금
  const [profileUnlocked, setProfileUnlocked] = useState(false);
  const [lockPw,      setLockPw]      = useState('');
  const [lockMsg,     setLockMsg]     = useState('');
  const [lockLoading, setLockLoading] = useState(false);

  // 비밀번호 변경
  const [currentPw, setCurrentPw] = useState('');
  const [newPw,     setNewPw]     = useState('');
  const [newPw2,    setNewPw2]    = useState('');
  const [pwMsg,     setPwMsg]     = useState('');
  const [pwSaving,  setPwSaving]  = useState(false);

  useEffect(() => {
    if (!user) return;
    supabase
      .from('orders')
      .select('*')
      .eq('user_id', user.id)
      .neq('status', 'cancelled')
      .order('created_at', { ascending: false })
      .then(({ data }) => { setOrders(data || []); setOrdersLoading(false); });
  }, [user]);

  useEffect(() => {
    if (!user) return;
    fetchMyCoupons(user.id)
      .then(setCoupons)
      .finally(() => setCouponsLoading(false));
  }, [user]);

  useEffect(() => {
    if (!user) return;
    supabase.from('inquiries').select('*').eq('user_id', user.id)
      .order('created_at', { ascending: false })
      .then(({ data }) => { setMyInquiries(data || []); setMyInquiriesLoading(false); });
  }, [user]);

  const openMyInquiry = async (inq) => {
    setSelectedInquiry(inq);
    const { data } = await supabase.from('inquiry_replies')
      .select('*').eq('inquiry_id', inq.id).order('created_at');
    setInquiryReplies(data || []);
  };

  const saveProfile = async (e) => {
    e.preventDefault();
    setProfileMsg('');
    if (!isValidName(displayName)) { setProfileMsg('이름은 한글 또는 영문 2~20자로 입력해 주세요. (숫자/특수문자 불가)'); return; }
    if (phone && !isValidPhone(phone)) { setProfileMsg('휴대폰번호 형식이 올바르지 않습니다. (예: 010-1234-5678)'); return; }
    if (zip && !isValidZip(zip)) { setProfileMsg('우편번호는 숫자 5자리로 입력해 주세요.'); return; }
    if (address1 && !isValidAddress1(address1)) { setProfileMsg('기본 주소를 정확히 입력해 주세요. (5자 이상, 숫자만 입력 불가)'); return; }

    setProfileSaving(true);
    const normalizedPhone = phone ? formatPhone(phone) : phone;
    const { error: authErr } = await supabase.auth.updateUser({ data: { display_name: displayName.trim() } });
    const { error: profErr } = await supabase.from('profiles').update({
      name: displayName.trim(), phone: normalizedPhone, zip, address1, address2,
      birthdate: birthdate || null, gender,
    }).eq('id', user.id);
    const error = authErr || profErr;
    setProfileMsg(error ? '저장 실패: ' + error.message : '저장되었습니다.');
    if (!error) { setPhone(normalizedPhone); refreshProfile(); }
    setProfileSaving(false);
  };

  const changePassword = async (e) => {
    e.preventDefault();
    setPwMsg('');
    if (!currentPw) { setPwMsg('현재 비밀번호를 입력해 주세요.'); return; }
    if (!isValidPassword(newPw)) { setPwMsg(`새 비밀번호는 ${PASSWORD_HINT}여야 합니다.`); return; }
    if (newPw !== newPw2) { setPwMsg('새 비밀번호가 일치하지 않습니다.'); return; }
    if (newPw === currentPw) { setPwMsg('현재 비밀번호와 다른 비밀번호를 사용해 주세요.'); return; }
    setPwSaving(true);

    // 현재 비밀번호 확인 (재인증)
    const { error: verifyErr } = await supabase.auth.signInWithPassword({ email: user.email, password: currentPw });
    if (verifyErr) {
      setPwMsg('현재 비밀번호가 올바르지 않습니다.');
      setPwSaving(false);
      return;
    }

    const { error } = await supabase.auth.updateUser({ password: newPw });
    if (error) {
      setPwMsg('변경 실패: ' + error.message);
    } else {
      setPwMsg('비밀번호가 변경되었습니다.');
      setCurrentPw(''); setNewPw(''); setNewPw2('');
    }
    setPwSaving(false);
  };

  const isSocialUser = ['kakao', 'naver'].includes(user?.app_metadata?.provider)
    || ['kakao', 'naver'].includes(user?.user_metadata?.provider);

  const openPostcode = () => {
    new window.daum.Postcode({
      oncomplete: (data) => {
        setZip(data.zonecode);
        setAddress1(data.roadAddress || data.jibunAddress);
        setAddress2('');
      },
    }).open();
  };

  const handleSignOut = async () => { await signOut(); onGoHome(); };

  const handleProfileUnlock = async (e) => {
    e.preventDefault();
    setLockMsg('');
    if (!lockPw) { setLockMsg('비밀번호를 입력해 주세요.'); return; }
    setLockLoading(true);
    const { error } = await supabase.auth.signInWithPassword({ email: user.email, password: lockPw });
    if (error) {
      setLockMsg('비밀번호가 올바르지 않습니다.');
    } else {
      setProfileUnlocked(true);
      setLockPw('');
      setLockMsg('');
    }
    setLockLoading(false);
  };

  if (!user) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 16 }}>
        <p style={{ fontFamily: "'Pretendard',sans-serif", fontSize: 13, color: C.muted }}>로그인이 필요합니다.</p>
        <button onClick={onGoHome} style={{ padding: '10px 28px', background: C.ink, color: C.bg, border: 'none', cursor: 'pointer', fontFamily: "'Pretendard',sans-serif", fontSize: 11, letterSpacing: 2 }}>홈으로</button>
      </div>
    );
  }

  return (
    <>
      <div style={{ maxWidth: 900, margin: '0 auto', padding: 'var(--page-top, 100px) 48px 0' }}>

        {/* 상단 인사 */}
        <div style={{ marginBottom: 40, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
          <div>
            <h1 style={{ fontFamily: "'DM Serif Display',serif", fontSize: 32, marginBottom: 6 }}>My Page</h1>
            <p style={{ fontFamily: "'Pretendard',sans-serif", fontSize: 12, color: C.muted, letterSpacing: 1 }}>{user.email}</p>
          </div>
          <button onClick={handleSignOut}
            style={{ background: 'none', border: `1px solid ${C.border}`, padding: '8px 18px', cursor: 'pointer', fontFamily: "'Pretendard',sans-serif", fontSize: 10, letterSpacing: 2, color: C.muted, transition: 'all .18s' }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = C.ink; e.currentTarget.style.color = C.ink; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.color = C.muted; }}>
            로그아웃
          </button>
        </div>

        {/* 탭 */}
        <div style={{ display: 'flex', borderBottom: `1px solid ${C.border}`, marginBottom: 40 }}>
          {[['orders', '주문 내역'], ['coupons', '내 쿠폰'], ['points', '적립금'], ['inquiries', '내 문의'], ['profile', '프로필 설정']].map(([key, label]) => (
            <button key={key} onClick={() => switchTab(key)} style={{
              padding: '12px 24px', background: 'none', border: 'none', cursor: 'pointer',
              fontFamily: "'Pretendard',sans-serif", fontWeight: 500, fontSize: 11, letterSpacing: 2,
              color: tab === key ? C.ink : C.muted,
              borderBottom: tab === key ? `2px solid ${C.ink}` : '2px solid transparent',
              marginBottom: -1, transition: 'color .18s',
            }}>{label}</button>
          ))}
        </div>

        {/* ── 주문 내역 ── */}
        {tab === 'orders' && (
          <div>
            {ordersLoading ? (
              <p style={{ fontFamily: "'Pretendard',sans-serif", fontSize: 12, color: C.muted }}>불러오는 중...</p>
            ) : orders.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '80px 0' }}>
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke={C.border} strokeWidth="1.2" style={{ marginBottom: 16 }}>
                  <path d="M6 2 3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/>
                  <path d="M16 10a4 4 0 01-8 0"/>
                </svg>
                <p style={{ fontFamily: "'Pretendard',sans-serif", fontSize: 12, color: C.muted, letterSpacing: 1 }}>주문 내역이 없습니다.</p>
              </div>
            ) : orders.map(order => (
              <OrderCard
                key={order.id}
                order={order}
                onCancelSuccess={(id) => setOrders(prev => prev.filter(o => o.id !== id))}
              />
            ))}
          </div>
        )}

        {/* ── 적립금 ── */}
        {tab === 'points' && (
          <div>
            {pointsLoading ? (
              <p style={{ fontFamily: "'Pretendard',sans-serif", fontSize: 12, color: C.muted }}>불러오는 중...</p>
            ) : (
              <>
                {/* 요약 */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 32 }}>
                  {[
                    { label: '총 적립', value: pointSummary.total_earned, color: C.ink },
                    { label: '사용',    value: pointSummary.total_used,   color: '#888' },
                    { label: '사용 가능', value: pointSummary.available,  color: '#2E7D52' },
                  ].map(({ label, value, color }) => (
                    <div key={label} style={{ border: `1px solid ${C.border}`, padding: '20px 24px', textAlign: 'center' }}>
                      <p style={{ fontFamily: "'Pretendard',sans-serif", fontSize: 10, letterSpacing: 2, color: C.muted, marginBottom: 8 }}>{label}</p>
                      <p style={{ fontFamily: "'Pretendard',sans-serif", fontSize: 22, fontWeight: 600, color }}>
                        {value.toLocaleString()}<span style={{ fontSize: 12, fontWeight: 400, marginLeft: 2 }}>원</span>
                      </p>
                    </div>
                  ))}
                </div>

                {/* 적립 예정 배너 */}
                {pendingPoints.length > 0 && (
                  <div style={{ background: '#FFF8E1', border: '1px solid #FFD54F', borderRadius: 6, padding: '14px 18px', marginBottom: 24, display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontSize: 18 }}>⏳</span>
                    <div>
                      <p style={{ fontFamily: "'Pretendard',sans-serif", fontWeight: 700, fontSize: 13, color: '#7A5500', margin: 0 }}>
                        적립 예정 — 포토 리뷰 확인 중
                      </p>
                      <p style={{ fontFamily: "'Pretendard',sans-serif", fontSize: 12, color: '#9A6B00', margin: '4px 0 0' }}>
                        포토 리뷰 {pendingPoints.length}건이 관리자 검토 중입니다. 확인 후 추가 적립금이 지급됩니다.
                      </p>
                    </div>
                  </div>
                )}

                {/* 내역 */}
                {pointTxns.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '80px 0' }}>
                    <p style={{ fontFamily: "'Pretendard',sans-serif", fontSize: 12, color: C.muted, letterSpacing: 1 }}>적립금 내역이 없습니다.</p>
                  </div>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ borderBottom: `2px solid ${C.ink}` }}>
                        {['내용', '구분', '금액', '유효기간', '날짜'].map(h => (
                          <th key={h} style={{ fontFamily: "'Pretendard',sans-serif", fontWeight: 500, fontSize: 10, letterSpacing: 2, color: '#888', padding: '8px 10px', textAlign: h === '내용' ? 'left' : 'center' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {pointTxns.map(tx => (
                        <tr key={tx.id} style={{ borderBottom: `1px solid ${C.border}` }}>
                          <td style={{ fontFamily: "'Pretendard',sans-serif", fontSize: 13, padding: '13px 10px', color: C.ink }}>{tx.description}</td>
                          <td style={{ padding: '13px 10px', textAlign: 'center' }}>
                            <span style={{ fontFamily: "'Pretendard',sans-serif", fontSize: 10, color: tx.amount > 0 ? '#2E7D52' : '#C94040', border: `1px solid ${tx.amount > 0 ? '#B2D8C5' : '#F5C6C6'}`, padding: '2px 7px', borderRadius: 2 }}>
                              {tx.amount > 0 ? '적립' : '사용'}
                            </span>
                          </td>
                          <td style={{ fontFamily: "'Pretendard',sans-serif", fontSize: 13, fontWeight: 600, padding: '13px 10px', textAlign: 'center', color: tx.amount > 0 ? '#2E7D52' : '#C94040' }}>
                            {tx.amount > 0 ? '+' : ''}{tx.amount.toLocaleString()}원
                          </td>
                          <td style={{ fontFamily: "'Pretendard',sans-serif", fontSize: 11, color: C.muted, padding: '13px 10px', textAlign: 'center' }}>
                            {tx.expires_at ? new Date(tx.expires_at).toLocaleDateString('ko-KR') : '-'}
                          </td>
                          <td style={{ fontFamily: "'Pretendard',sans-serif", fontSize: 11, color: C.muted, padding: '13px 10px', textAlign: 'center', whiteSpace: 'nowrap' }}>
                            {new Date(tx.created_at).toLocaleDateString('ko-KR')}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </>
            )}
          </div>
        )}

        {/* ── 내 쿠폰 ── */}
        {tab === 'coupons' && (
          <div>
            {couponsLoading ? (
              <p style={{ fontFamily: "'Pretendard',sans-serif", fontSize: 12, color: C.muted }}>불러오는 중...</p>
            ) : coupons.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '80px 0' }}>
                <p style={{ fontFamily: "'Pretendard',sans-serif", fontSize: 12, color: C.muted, letterSpacing: 1 }}>보유한 쿠폰이 없습니다.</p>
              </div>
            ) : coupons.map(uc => {
              const status = COUPON_STATUS_LABEL[uc.status] || COUPON_STATUS_LABEL.unused;
              const c = uc.coupons || {};
              const benefit = c.type === 'percent'
                ? `${c.value}%${c.max_discount ? ` (최대 ₩${c.max_discount.toLocaleString()})` : ''}`
                : `₩${(c.value || 0).toLocaleString()}`;
              return (
                <div key={uc.id} style={{ border: `1px solid ${C.border}`, padding: '18px 20px', marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <p style={{ fontFamily: "'Presentation5',sans-serif", fontSize: 14, marginBottom: 4 }}>{c.title}</p>
                    <p style={{ fontFamily: "'Pretendard',sans-serif", fontSize: 11, color: C.muted, letterSpacing: 1 }}>
                      {benefit} 할인{c.min_order_amount ? ` · ₩${c.min_order_amount.toLocaleString()} 이상 주문 시` : ''}
                      {c.expires_at ? ` · ${new Date(c.expires_at).toLocaleDateString('ko-KR')}까지` : ''}
                    </p>
                  </div>
                  <span style={{ fontFamily: "'Pretendard',sans-serif", fontWeight: 500, fontSize: 11, letterSpacing: 1, color: status.color, padding: '4px 10px', border: `1px solid ${status.color}`, borderRadius: 2 }}>
                    {status.text}
                  </span>
                </div>
              );
            })}
          </div>
        )}

        {/* ── 내 문의 ── */}
        {tab === 'inquiries' && (
          selectedInquiry ? (
            <div style={{ maxWidth: 720 }}>
              <button onClick={() => { setSelectedInquiry(null); setInquiryReplies([]); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.muted, fontFamily: "'Pretendard',sans-serif", fontSize: 12, marginBottom: 24, padding: 0 }}>
                ← 목록으로
              </button>
              <div style={{ border: `1px solid ${C.border}`, padding: '24px 28px', marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                  <p style={{ fontFamily: "'Pretendard',sans-serif", fontWeight: 700, fontSize: 16 }}>{selectedInquiry.title}</p>
                  <span style={{ fontSize: 10, fontFamily: "'Pretendard',sans-serif", padding: '2px 8px',
                    background: selectedInquiry.status === 'answered' ? '#E6F4ED' : '#F5F5F5',
                    color: selectedInquiry.status === 'answered' ? '#2E7D52' : '#888',
                    border: `1px solid ${selectedInquiry.status === 'answered' ? '#B2D8C5' : '#DDD'}` }}>
                    {selectedInquiry.status === 'answered' ? '답변 완료' : '답변 대기'}
                  </span>
                  <span style={{ fontSize: 10, fontFamily: "'Pretendard',sans-serif", padding: '2px 8px', background: '#F5F5F5', color: '#888', border: '1px solid #DDD' }}>
                    {selectedInquiry.is_public ? '공개' : '비공개'}
                  </span>
                </div>
                <p style={{ fontFamily: "'Pretendard',sans-serif", fontSize: 11, color: C.muted, marginBottom: 20 }}>
                  {new Date(selectedInquiry.created_at).toLocaleDateString('ko-KR')}
                </p>
                <div style={{ fontFamily: "'Pretendard',sans-serif", fontSize: 13, lineHeight: 1.9, color: '#333', whiteSpace: 'pre-wrap' }}>{selectedInquiry.content}</div>
              </div>
              {inquiryReplies.length > 0 ? inquiryReplies.map(r => (
                <div key={r.id} style={{ background: '#F7F5F2', border: `1px solid ${C.border}`, padding: '20px 24px', marginBottom: 12 }}>
                  <p style={{ fontFamily: "'Pretendard',sans-serif", fontWeight: 600, fontSize: 11, letterSpacing: 1.5, color: C.accent, marginBottom: 12 }}>UNDERLOOKED 답변</p>
                  <div style={{ fontFamily: "'Pretendard',sans-serif", fontSize: 13, lineHeight: 1.9, whiteSpace: 'pre-wrap' }}>{r.content}</div>
                  <p style={{ fontFamily: "'Pretendard',sans-serif", fontSize: 10, color: C.muted, marginTop: 10 }}>{new Date(r.created_at).toLocaleDateString('ko-KR')}</p>
                </div>
              )) : (
                <div style={{ padding: '20px 24px', background: '#FAFAFA', border: `1px solid ${C.border}` }}>
                  <p style={{ fontFamily: "'Pretendard',sans-serif", fontSize: 12, color: C.muted, textAlign: 'center' }}>답변 준비 중입니다. 빠른 시일 내에 답변 드리겠습니다.</p>
                </div>
              )}
            </div>
          ) : (
            <div>
              {myInquiriesLoading ? (
                <p style={{ fontFamily: "'Pretendard',sans-serif", fontSize: 12, color: C.muted }}>불러오는 중...</p>
              ) : myInquiries.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '80px 0' }}>
                  <p style={{ fontFamily: "'Pretendard',sans-serif", fontSize: 12, color: C.muted, letterSpacing: 1 }}>작성한 문의가 없습니다.</p>
                </div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: `2px solid ${C.ink}` }}>
                      {['제목', '공개', '상태', '작성일'].map(h => (
                        <th key={h} style={{ fontFamily: "'Pretendard',sans-serif", fontWeight: 500, fontSize: 10, letterSpacing: 2, color: C.muted, padding: '8px 12px', textAlign: h === '제목' ? 'left' : 'center' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {myInquiries.map(inq => (
                      <tr key={inq.id} style={{ borderBottom: `1px solid ${C.border}`, cursor: 'pointer' }}
                        onClick={() => openMyInquiry(inq)}
                        onMouseEnter={e => e.currentTarget.style.background = '#F7F7F5'}
                        onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                        <td style={{ fontFamily: "'Pretendard',sans-serif", fontSize: 13, padding: '14px 12px', color: C.ink }}>{inq.title}</td>
                        <td style={{ fontFamily: "'Pretendard',sans-serif", fontSize: 11, color: C.muted, padding: '14px 12px', textAlign: 'center' }}>{inq.is_public ? '공개' : '비공개'}</td>
                        <td style={{ padding: '14px 12px', textAlign: 'center' }}>
                          <span style={{ fontSize: 10, fontFamily: "'Pretendard',sans-serif", padding: '2px 8px',
                            background: inq.status === 'answered' ? '#E6F4ED' : '#F5F5F5',
                            color: inq.status === 'answered' ? '#2E7D52' : '#888',
                            border: `1px solid ${inq.status === 'answered' ? '#B2D8C5' : '#DDD'}` }}>
                            {inq.status === 'answered' ? '답변 완료' : '답변 대기'}
                          </span>
                        </td>
                        <td style={{ fontFamily: "'Pretendard',sans-serif", fontSize: 11, color: C.muted, padding: '14px 12px', textAlign: 'center', whiteSpace: 'nowrap' }}>
                          {new Date(inq.created_at).toLocaleDateString('ko-KR')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )
        )}

        {/* ── 프로필 설정 ── */}
        {tab === 'profile' && !profileUnlocked && !isSocialUser && (
          <div style={{ maxWidth: 360, margin: '0 auto', padding: '60px 0' }}>
            <p style={{ fontFamily: "'Pretendard',sans-serif", fontWeight: 500, fontSize: 10, letterSpacing: 3, marginBottom: 24, textAlign: 'center' }}>본인 확인</p>
            <p style={{ fontFamily: "'Pretendard',sans-serif", fontSize: 12, color: C.muted, textAlign: 'center', marginBottom: 28, lineHeight: 1.7 }}>
              프로필 설정에 접근하려면<br />현재 비밀번호를 입력해 주세요.
            </p>
            <form onSubmit={handleProfileUnlock} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <label className="adm-label">현재 비밀번호</label>
                <input className="adm-input" type="password" value={lockPw} onChange={e => setLockPw(e.target.value)} placeholder="비밀번호 입력" autoComplete="current-password" autoFocus />
              </div>
              {lockMsg && (
                <p style={{ fontFamily: "'Pretendard',sans-serif", fontSize: 12, color: '#C94040' }}>{lockMsg}</p>
              )}
              <button type="submit" disabled={lockLoading}
                style={{ padding: '12px 0', background: C.ink, color: C.bg, border: 'none', cursor: 'pointer', fontFamily: "'Pretendard',sans-serif", fontWeight: 500, fontSize: 11, letterSpacing: 2, opacity: lockLoading ? .6 : 1 }}>
                {lockLoading ? '확인 중...' : '확인'}
              </button>
            </form>
          </div>
        )}

        {tab === 'profile' && (profileUnlocked || isSocialUser) && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 48 }}>

            {/* 이름 변경 */}
            <div>
              <p style={{ fontFamily: "'Pretendard',sans-serif", fontWeight: 500, fontSize: 10, letterSpacing: 3, marginBottom: 20 }}>기본 정보</p>
              <form onSubmit={saveProfile} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div>
                  <label className="adm-label">이메일</label>
                  <input className="adm-input" value={user.email} disabled
                    style={{ background: C.bg2, color: C.muted, cursor: 'not-allowed' }} />
                </div>
                {isSocialUser ? (
                  <div>
                    <label className="adm-label">로그인 방식</label>
                    <div style={{
                      display: 'inline-flex', alignItems: 'center', gap: 8,
                      padding: '8px 14px', border: `1px solid ${C.border}`,
                      borderRadius: 3, background: C.bg2,
                    }}>
                      {(user?.user_metadata?.provider === 'naver' || user?.app_metadata?.provider === 'naver') ? (
                        <>
                          <span style={{ width: 16, height: 16, background: '#03C75A', borderRadius: 3, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontFamily: 'sans-serif', fontSize: 10, fontWeight: 700 }}>N</span>
                          <span style={{ fontFamily: "'Pretendard',sans-serif", fontSize: 13, color: C.ink }}>네이버 계정으로 로그인 중</span>
                        </>
                      ) : (
                        <>
                          <span style={{ width: 16, height: 16, background: '#FEE500', borderRadius: 3, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'sans-serif', fontSize: 10, fontWeight: 700 }}>K</span>
                          <span style={{ fontFamily: "'Pretendard',sans-serif", fontSize: 13, color: C.ink }}>카카오 계정으로 로그인 중</span>
                        </>
                      )}
                    </div>
                  </div>
                ) : (
                  <div>
                    <label className="adm-label">아이디</label>
                    <input className="adm-input" value={profile?.username || ''} disabled
                      style={{ background: C.bg2, color: C.muted, cursor: 'not-allowed' }} />
                  </div>
                )}
                <div>
                  <label className="adm-label">이름</label>
                  <input className="adm-input" value={displayName} onChange={e => setDisplayName(e.target.value)} placeholder="홍길동" />
                </div>
                <div>
                  <label className="adm-label">휴대폰번호</label>
                  <input className="adm-input" value={phone} onChange={e => setPhone(e.target.value)} placeholder="010-0000-0000" />
                </div>
                <div>
                  <label className="adm-label">생년월일</label>
                  <input className="adm-input" type="date" value={birthdate || ''} onChange={e => setBirthdate(e.target.value)} />
                </div>
                <div>
                  <label className="adm-label">성별</label>
                  <div style={{ display: 'flex', gap: 8 }}>
                    {[['male','남'], ['female','여'], ['none','선택안함']].map(([val, label]) => (
                      <button key={val} type="button" onClick={() => setGender(val)} style={{
                        flex: 1, padding: '9px 0', border: `1px solid ${gender === val ? C.ink : C.border}`,
                        background: gender === val ? C.ink : 'none', color: gender === val ? C.bg : C.ink,
                        cursor: 'pointer', fontFamily: "'Pretendard',sans-serif", fontSize: 11, letterSpacing: 1,
                        transition: 'all .15s',
                      }}>{label}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="adm-label">우편번호</label>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input className="adm-input" style={{ maxWidth: 120 }} value={zip} readOnly placeholder="12345" />
                    <button type="button" onClick={openPostcode}
                      style={{ padding: '0 14px', background: 'none', border: `1px solid ${C.border}`, cursor: 'pointer', fontFamily: "'Pretendard',sans-serif", fontSize: 10, letterSpacing: 1.5, color: C.ink, whiteSpace: 'nowrap', transition: 'border-color .15s' }}
                      onMouseEnter={e => e.currentTarget.style.borderColor = C.ink}
                      onMouseLeave={e => e.currentTarget.style.borderColor = C.border}>
                      주소 검색
                    </button>
                  </div>
                </div>
                <div>
                  <label className="adm-label">기본 주소</label>
                  <input className="adm-input" value={address1} readOnly placeholder="주소 검색 후 자동 입력" />
                </div>
                <div>
                  <label className="adm-label">상세 주소</label>
                  <input className="adm-input" value={address2} onChange={e => setAddress2(e.target.value)} placeholder="101동 202호" />
                </div>
                {profileMsg && (
                  <p style={{ fontFamily: "'Pretendard',sans-serif", fontSize: 12, color: profileMsg.includes('실패') ? '#C94040' : '#3D8B5C' }}>{profileMsg}</p>
                )}
                <button type="submit" disabled={profileSaving}
                  style={{ padding: '12px 0', background: C.ink, color: C.bg, border: 'none', cursor: 'pointer', fontFamily: "'Pretendard',sans-serif", fontWeight: 500, fontSize: 11, letterSpacing: 2, opacity: profileSaving ? .6 : 1 }}>
                  {profileSaving ? '저장 중...' : '저장'}
                </button>
              </form>
            </div>

            {/* 비밀번호 변경 - 소셜 로그인 유저 제외 */}
            {!isSocialUser && <div>
              <p style={{ fontFamily: "'Pretendard',sans-serif", fontWeight: 500, fontSize: 10, letterSpacing: 3, marginBottom: 20 }}>비밀번호 변경</p>
              <form onSubmit={changePassword} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div>
                  <label className="adm-label">현재 비밀번호</label>
                  <input className="adm-input" type="password" value={currentPw} onChange={e => setCurrentPw(e.target.value)} placeholder="현재 비밀번호" autoComplete="current-password" />
                </div>
                <div>
                  <label className="adm-label">새 비밀번호</label>
                  <input className="adm-input" type="password" value={newPw} onChange={e => setNewPw(e.target.value)} placeholder={PASSWORD_HINT} autoComplete="new-password" />
                </div>
                <div>
                  <label className="adm-label">새 비밀번호 확인</label>
                  <input className="adm-input" type="password" value={newPw2} onChange={e => setNewPw2(e.target.value)} placeholder="다시 입력" autoComplete="new-password" />
                </div>
                {pwMsg && (
                  <p style={{ fontFamily: "'Pretendard',sans-serif", fontSize: 12, color: pwMsg.includes('변경되었') ? '#3D8B5C' : '#C94040' }}>{pwMsg}</p>
                )}
                <button type="submit" disabled={pwSaving}
                  style={{ padding: '12px 0', background: C.ink, color: C.bg, border: 'none', cursor: 'pointer', fontFamily: "'Pretendard',sans-serif", fontWeight: 500, fontSize: 11, letterSpacing: 2, opacity: pwSaving ? .6 : 1 }}>
                  {pwSaving ? '변경 중...' : '비밀번호 변경'}
                </button>
              </form>

            </div>}
          </div>
        )}

      </div>
      <Footer nav={nav} />
    </>
  );
}

function OrderCard({ order, onCancelSuccess }) {
  const [open, setOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const status = STATUS_LABEL[order.status] || { text: order.status, color: C.muted };
  const date = new Date(order.created_at).toLocaleDateString('ko-KR', { year: 'numeric', month: 'long', day: 'numeric' });
  const canCancel = !['shipping', 'delivered', 'cancelled'].includes(order.status);

  const handleCancel = async () => {
    if (!window.confirm('주문을 취소하시겠습니까?')) return;
    setCancelling(true);
    const { data, error } = await supabase.rpc('cancel_my_order', { p_order_id: order.id });
    setCancelling(false);
    if (error || !data) { alert('취소 처리 중 오류가 발생했습니다.'); return; }
    onCancelSuccess(order.id);
  };

  return (
    <div style={{ border: `1px solid ${C.border}`, marginBottom: 16 }}>
      {/* 주문 헤더 */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', cursor: 'pointer', background: open ? C.bg2 : 'transparent', transition: 'background .15s' }}
        onClick={() => setOpen(o => !o)}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
          <div>
            <p style={{ fontFamily: "'Pretendard',sans-serif", fontSize: 10, color: C.muted, letterSpacing: 1, marginBottom: 4 }}>{date}</p>
            <p style={{ fontFamily: "'Pretendard',sans-serif", fontWeight: 500, fontSize: 13, letterSpacing: 1 }}>
              {(order.items || []).map(i => i.productName).filter(Boolean).slice(0, 1).join('')}
              {(order.items || []).length > 1 ? ` 외 ${(order.items || []).length - 1}건` : ''}
            </p>
          </div>
          <span style={{ fontFamily: "'Pretendard',sans-serif", fontWeight: 500, fontSize: 11, letterSpacing: 1, color: status.color, padding: '4px 10px', border: `1px solid ${status.color}`, borderRadius: 2 }}>
            {status.text}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
          <span style={{ fontFamily: "'Presentation5',sans-serif", fontSize: 15 }}>₩{order.total.toLocaleString()}</span>
          <span style={{ fontSize: 12, color: C.muted, transition: 'transform .2s', display: 'inline-block', transform: open ? 'rotate(180deg)' : 'none' }}>▾</span>
        </div>
      </div>

      {/* 주문 상세 */}
      {open && (
        <div style={{ borderTop: `1px solid ${C.border}`, padding: '20px' }}>
          {/* 주문번호 */}
          <p style={{ fontFamily: "'Pretendard',sans-serif", fontSize: 10, color: C.muted, letterSpacing: 1, marginBottom: 16 }}>
            주문번호: {order.order_number || order.id.slice(0, 8).toUpperCase()}
          </p>
          {/* 상품 목록 */}
          <p style={{ fontFamily: "'Pretendard',sans-serif", fontWeight: 500, fontSize: 10, letterSpacing: 2, marginBottom: 12 }}>주문 상품</p>
          {(order.items || []).map((item, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: `1px solid ${C.border}` }}>
              <div>
                <p style={{ fontFamily: "'Presentation5',sans-serif", fontSize: 13, marginBottom: 3 }}>{item.productName}</p>
                <p style={{ fontFamily: "'Pretendard',sans-serif", fontSize: 10, color: C.muted, letterSpacing: 1 }}>
                  {item.size}{item.colorName ? ` · ${item.colorName}` : ''} · {item.quantity}개
                </p>
              </div>
              <p style={{ fontFamily: "'Presentation5',sans-serif", fontSize: 13 }}>₩{(item.price * item.quantity).toLocaleString()}</p>
            </div>
          ))}

          {/* 배송지 */}
          <div style={{ marginTop: 20, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
            <div>
              <p style={{ fontFamily: "'Pretendard',sans-serif", fontWeight: 500, fontSize: 10, letterSpacing: 2, marginBottom: 10 }}>배송지</p>
              {[
                [order.shipping?.name, '수령인'],
                [order.shipping?.phone, '연락처'],
                [`(${order.shipping?.zip}) ${order.shipping?.address1} ${order.shipping?.address2 || ''}`, '주소'],
                [order.shipping?.note, '배송 메모'],
              ].filter(([v]) => v).map(([val, label]) => (
                <div key={label} style={{ display: 'flex', gap: 12, marginBottom: 6 }}>
                  <span style={{ fontFamily: "'Pretendard',sans-serif", fontSize: 10, color: C.muted, letterSpacing: 1, minWidth: 56 }}>{label}</span>
                  <span style={{ fontFamily: "'Pretendard',sans-serif", fontSize: 11 }}>{val}</span>
                </div>
              ))}
            </div>
            <div>
              <p style={{ fontFamily: "'Pretendard',sans-serif", fontWeight: 500, fontSize: 10, letterSpacing: 2, marginBottom: 10 }}>결제 금액</p>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                <span style={{ fontFamily: "'Pretendard',sans-serif", fontSize: 11, color: C.muted }}>총 결제</span>
                <span style={{ fontFamily: "'Presentation5',sans-serif", fontSize: 15 }}>₩{order.total.toLocaleString()}</span>
              </div>
            </div>
          </div>

          {/* 주문 취소 */}
          <div style={{ marginTop: 16, paddingTop: 16, borderTop: `1px solid ${C.border}`, display: 'flex', justifyContent: 'flex-end' }}>
            {canCancel ? (
              <button
                onClick={handleCancel}
                disabled={cancelling}
                style={{
                  padding: '8px 20px', background: 'none',
                  border: '1px solid #C94040', color: '#C94040',
                  fontFamily: "'Pretendard',sans-serif", fontSize: 11, letterSpacing: 1,
                  cursor: cancelling ? 'wait' : 'pointer', opacity: cancelling ? .6 : 1,
                  transition: 'all .15s',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = '#C94040'; e.currentTarget.style.color = '#fff'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = '#C94040'; }}
              >
                {cancelling ? '취소 중...' : '주문 취소'}
              </button>
            ) : (
              <span style={{ fontFamily: "'Pretendard',sans-serif", fontSize: 11, color: C.muted }}>
                {order.status === 'cancelled' ? '취소된 주문입니다' : '배송 시작 후 취소 불가'}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
